/**
 * Dashboard (`app/services/dashboard_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import type { DashboardAnalysis } from '@brainfocus/web/api/types';

export const getDashboard = client.endpointJson<void, DashboardResponse>('/api/dashboard');

export interface DashboardRecording {
    edfId: number;
    title: string;
    filename: string;
    patientId: number;
    patientName: string;
    acquisitionDate: number | null;
    duration: number | null;
    uploaderId: number;
    uploaderName: string | null;
    analyses: DashboardAnalysis[];
    totalAnalyses: number;
    /** The current user annotated it or created a non-pending analysis of it (#374). */
    reviewed: boolean;
    /** Epoch ms of the current user's most recent annotation / analysis on it (#374). */
    lastActivityAt: number | null;
    /** Where the current user should resume it, in recording seconds (#374). */
    resumeSec: number | null;
    /** Recording start as wall-clock encoded in UTC components (as `EdfInfo`), for the Resume time (#374). */
    recStartWallClockMs: number | null;
}

export interface DashboardNeuroimage {
    id: number;
    name: string;
    patientId: number;
    patientName: string;
    uploaderId: number;
    uploaderName: string | null;
}

export interface DashboardPatient {
    id: number;
    name: string;
    totalRecordings: number;
    totalNeuroimages: number;
    /** How many of the patient's recordings the current user reviewed (#374). */
    reviewedCount: number;
    /** Epoch ms of the current user's most recent activity on this patient (#374). */
    lastActivityAt: number | null;
    /** The recording the review indicator jumps back to (#374). */
    resumeEdfId: number | null;
}

export interface DashboardResponse {
    recordings: DashboardRecording[];
    neuroimages: DashboardNeuroimage[];
    patients: DashboardPatient[];
    /** The patient the current user last worked on, pinned first (#374). */
    lastPatientId: number | null;
}
